import { useState, useEffect } from 'react'
import { Box, Snackbar, Alert } from '@mui/material'
import DashboardNavbar from '../components/layout/DashboardNavbar'
import ControlsPanel from '../components/dashboard/ControlsPanel'
import PreviewPanel from '../components/dashboard/PreviewPanel'
import SubscriptionModal from '../components/subscription/SubscriptionModal'
import imageGenerationService from '../services/imageGenerationService'
import subscriptionService from '../services/subscriptionService'
import { useAuth } from '../context/AuthContext'
import { trackEvent } from '../utils/analytics'
import { downloadImage } from '../utils/downloadImage'

const Dashboard = () => {
  const { user } = useAuth()
  const [prompt, setPrompt] = useState('')
  const [uploadedFile, setUploadedFile] = useState(null)
  const [uploadedPreview, setUploadedPreview] = useState(null)
  const [state, setState] = useState('empty') // 'empty' | 'uploaded' | 'loading' | 'results' | 'error'
  const [results, setResults] = useState([])
  const [errorMessage, setErrorMessage] = useState('')

  // Subscription state
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false)
  const [subscriptionLoading, setSubscriptionLoading] = useState(true)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)

  // Toast notification state
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState('success') // 'success' | 'error' | 'info'

  // Restore pending generation data from sessionStorage (if returning from payment)
  useEffect(() => {
    const restorePendingGeneration = () => {
      const pendingData = sessionStorage.getItem('pending_generation')
      if (pendingData) {
        try {
          const { imageDataUrl, fileName, fileType, prompt: savedPrompt } = JSON.parse(pendingData)

          // Convert base64 back to File
          fetch(imageDataUrl)
            .then((res) => res.blob())
            .then((blob) => {
              const file = new File([blob], fileName, { type: fileType })
              setUploadedFile(file)
              setUploadedPreview(imageDataUrl)
              setPrompt(savedPrompt)
              setState('uploaded')
            })
            .catch((error) => {
              console.error('Error restoring file:', error)
            })

          // Clear the pending data
          sessionStorage.removeItem('pending_generation')
        } catch (error) {
          console.error('Error parsing pending generation data:', error)
          sessionStorage.removeItem('pending_generation')
        }
      }
    }

    restorePendingGeneration()
  }, [])

  // Check subscription status on mount
  useEffect(() => {
    const checkSubscription = async () => {
      if (user) {
        try {
          const result = await subscriptionService.getSubscriptionStatus()

          console.log('Subscription check result:', result)

          if (result.success) {
            // Use the returned subscription status
            setHasActiveSubscription(result.hasActiveSubscription)
          } else {
            // On API error, fail closed (block access) unless it's a network error
            console.error('Subscription check failed:', result.error)
            setHasActiveSubscription(false)
          }
        } catch (error) {
          console.error('Error checking subscription:', error)
          // On unexpected error, fail closed (block access)
          setHasActiveSubscription(false)
        } finally {
          setSubscriptionLoading(false)
        }
      } else {
        // No user, no subscription
        setHasActiveSubscription(false)
        setSubscriptionLoading(false)
      }
    }

    checkSubscription()
  }, [user])

  // Check for payment success URL parameter
  useEffect(() => {
    const handlePaymentSuccess = async () => {
      const urlParams = new URLSearchParams(window.location.search)
      const paymentSuccess = urlParams.get('payment_success')

      if (paymentSuccess === 'true') {
        console.log('Payment success detected, clearing cache and checking subscription...')

        // Clear subscription cache to force fresh check
        subscriptionService.clearSubscriptionCache()

        // Poll for subscription activation (backend webhook may take time)
        let subscriptionActive = false
        let attempts = 0
        const maxAttempts = 5
        const delayBetweenAttempts = 2000 // 2 seconds

        while (!subscriptionActive && attempts < maxAttempts) {
          attempts++
          console.log(`Checking subscription status (attempt ${attempts}/${maxAttempts})...`)

          // Wait before checking (except first attempt)
          if (attempts > 1) {
            await new Promise((resolve) => setTimeout(resolve, delayBetweenAttempts))
          } else {
            await new Promise((resolve) => setTimeout(resolve, 1000)) // 1 sec first attempt
          }

          try {
            const result = await subscriptionService.getSubscriptionStatus(true) // Force refresh
            console.log(`Subscription status (attempt ${attempts}):`, result)

            if (result.success && result.hasActiveSubscription) {
              subscriptionActive = true
              break
            }
          } catch (error) {
            console.error(`Error checking subscription (attempt ${attempts}):`, error)
          }
        }

        // Process result
        try {
          if (subscriptionActive) {
            console.log('Subscription is active! Setting state and triggering generation...')
            setHasActiveSubscription(true)

            // Auto-trigger generation if file and prompt exist
            if (uploadedFile && prompt.trim()) {
              console.log('File and prompt found, starting generation...')

              // Show success toast
              setToastMessage('Payment completed successfully! Generating your images...')
              setToastSeverity('success')
              setShowToast(true)

              // Set loading state and start generation
              setState('loading')
              setErrorMessage('')

              // Call generation directly instead of through handleGenerate
              setTimeout(async () => {
                try {
                  console.log('Calling backend API to generate images...')
                  const result = await imageGenerationService.generateImages(uploadedFile, prompt)

                  if (result.success && result.images?.length > 0) {
                    setResults(result.images)
                    setState('results')
                  } else {
                    setErrorMessage(result.message || 'No images were generated. Please try again with a different prompt.')
                    setState('error')
                  }
                } catch (error) {
                  console.error('Image generation failed:', error)
                  setErrorMessage(error.message || 'An unexpected error occurred. Please try again.')
                  setState('error')
                }
              }, 500)
            } else {
              console.log('No file or prompt found')
              // No file/prompt, just show success message
              setToastMessage('Payment completed successfully!')
              setToastSeverity('success')
              setShowToast(true)
            }
          } else {
            console.warn(`Subscription not active after ${maxAttempts} attempts`)
            // Payment success but subscription not active after retries
            setToastMessage(
              `Payment processed, but subscription not yet active. This may take a moment. Please refresh the page in 10 seconds.`
            )
            setToastSeverity('warning')
            setShowToast(true)
          }
        } catch (error) {
          console.error('Error processing subscription after payment:', error)
          setToastMessage('Payment completed! Please refresh the page to continue.')
          setToastSeverity('info')
          setShowToast(true)
        }

        // Clean URL (remove payment_success parameter)
        const newUrl = window.location.pathname
        window.history.replaceState({}, '', newUrl)
      }
    }

    // Only run if user is logged in and component has mounted
    if (user) {
      handlePaymentSuccess()
    }
  }, [user, uploadedFile, prompt])

  // Mock generated images (from the HTML design)
  const mockResults = [
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/3759442772-e05a1036d2b67538cf20.png',
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/9a62102fbf-5184abe3e5c0937255aa.png',
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/f2a1effed6-bbec64592afdfd5fc501.png',
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/f59247bcce-885e42ae08ea26b53c52.png',
  ]

  const handleFileSelect = (file) => {
    setUploadedFile(file)

    // Create preview URL
    const reader = new FileReader()
    reader.onloadend = () => {
      setUploadedPreview(reader.result)
      setState('uploaded')
    }
    reader.readAsDataURL(file)
  }

  const handlePromptChange = (newPrompt) => {
    setPrompt(newPrompt)
  }

  const handleGenerate = async () => {
    // Track button click event
    trackEvent('Dashboard', 'Generate Images', 'Generate Button Click')

    // Check subscription before generating
    if (!hasActiveSubscription) {
      setShowSubscriptionModal(true)
      return
    }

    setState('loading')
    setErrorMessage('')

    try {
      // Call backend API to generate images
      const result = await imageGenerationService.generateImages(uploadedFile, prompt)

      if (result.success && result.images?.length > 0) {
        setResults(result.images)
        setState('results')
      } else {
        // Handle specific error types
        if (result.error === 'SUBSCRIPTION_REQUIRED') {
          setShowSubscriptionModal(true)
          setState('uploaded')
          return
        }
        if (result.error === 'CREDITS_EXHAUSTED') {
          setErrorMessage(
            "You've used all 150 AI credits for this month. Credits reset on your billing date."
          )
          setState('error')
          return
        }
        if (result.error === 'SESSION_EXPIRED') {
          setErrorMessage('Your session has expired. Please log in again.')
          setState('error')
          return
        }

        setErrorMessage(result.message || 'No images were generated. Please try again with a different prompt.')
        setState('error')
      }
    } catch (error) {
      console.error('Image generation failed:', error)
      setErrorMessage(error.message || 'An unexpected error occurred. Please try again.')
      setState('error')
    }
  }

  const handleCloseSubscriptionModal = () => {
    setShowSubscriptionModal(false)
  }

  const handleCloseToast = (event, reason) => {
    // Don't close if user clicks away (only manual close or timeout)
    if (reason === 'clickaway') {
      return
    }
    setShowToast(false)
  }

  const handleDelete = () => {
    setUploadedFile(null)
    setUploadedPreview(null)
    setState('empty')
    setPrompt('')
  }

  const handleReplace = () => {
    // Trigger file input
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = (e) => {
      const file = e.target.files[0]
      if (file) {
        handleFileSelect(file)
      }
    }
    input.click()
  }

  const handleRegenerate = async () => {
    // Track button click event
    trackEvent('Dashboard', 'Generate More Images', 'Generate More Button Click')

    // Check subscription before generating
    if (!hasActiveSubscription) {
      setShowSubscriptionModal(true)
      return
    }

    setState('loading')
    setErrorMessage('')

    try {
      // Call backend API to generate more images
      const result = await imageGenerationService.generateImages(uploadedFile, prompt)

      if (result.success && result.images?.length > 0) {
        // Prepend new images to existing results
        setResults([...result.images, ...results])
        setState('results')
      } else {
        // Handle specific error types
        if (result.error === 'SUBSCRIPTION_REQUIRED') {
          setShowSubscriptionModal(true)
          setState('results') // Keep showing existing results
          return
        }
        if (result.error === 'CREDITS_EXHAUSTED') {
          setErrorMessage(
            "You've used all 150 AI credits for this month. Credits reset on your billing date."
          )
          setState('error')
          return
        }
        if (result.error === 'SESSION_EXPIRED') {
          setErrorMessage('Your session has expired. Please log in again.')
          setState('error')
          return
        }

        setErrorMessage(result.message || 'No images were generated. Please try again with a different prompt.')
        setState('error')
      }
    } catch (error) {
      console.error('Image generation failed:', error)
      setErrorMessage(error.message || 'An unexpected error occurred. Please try again.')
      setState('error')
    }
  }

  const handleDownloadAll = async () => {
    // Download all images sequentially to avoid overwhelming the browser
    for (let index = 0; index < results.length; index++) {
      await downloadImage(results[index], `mocksy-generated-${index + 1}.png`)
      // Small delay between downloads
      if (index < results.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 300))
      }
    }
  }

  const handleRetry = () => {
    handleGenerate()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <DashboardNavbar />

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <ControlsPanel
          prompt={prompt}
          onPromptChange={handlePromptChange}
          onFileSelect={handleFileSelect}
          onGenerate={handleGenerate}
          isGenerating={state === 'loading'}
          hasFile={!!uploadedFile}
          uploadedPreview={uploadedPreview}
          onDelete={handleDelete}
          onReplace={handleReplace}
        />

        <PreviewPanel
          state={state}
          results={results}
          errorMessage={errorMessage}
          onRegenerate={handleRegenerate}
          onDownloadAll={handleDownloadAll}
          onRetry={handleRetry}
        />
      </Box>

      {/* Subscription Modal */}
      <SubscriptionModal
        open={showSubscriptionModal}
        onClose={handleCloseSubscriptionModal}
        uploadedFile={uploadedFile}
        prompt={prompt}
      />

      {/* Toast Notification */}
      <Snackbar
        open={showToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 2 }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toastSeverity}
          sx={{
            width: '100%',
            minWidth: 400,
            boxShadow: 3,
            fontSize: '1rem',
            fontWeight: 600,
            py: 1.5,
            px: 2,
            '& .MuiAlert-icon': {
              fontSize: '1.5rem',
            },
            ...(toastSeverity === 'success' && {
              bgcolor: '#10b981',
              color: 'white',
              '& .MuiAlert-icon': {
                color: 'white',
              },
              '& .MuiIconButton-root': {
                color: 'white',
              },
            }),
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Dashboard
